import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginUser, fetchUserDetails } from "../../../Redux/slice/features/authSlice";
import { REACT_APP_API_URL } from "../../../env";

const Signin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, error } = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });

  const [captchaText, setCaptchaText] = useState("");
  const [captchaId, setCaptchaId] = useState("");
  const [captchaValue, setCaptchaValue] = useState("");
  const [toast, setToast] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadCaptcha = async () => {
    try {
      const res = await fetch(`${REACT_APP_API_URL}api/hrms/auth/captcha`);
      const data = await res.json();
      setCaptchaText(data.captcha);
      setCaptchaId(data.captchaId);
      setCaptchaValue("");
    } catch {
      setToast("Unable to load captcha");
      setTimeout(() => setToast(""), 4000);
    }
  };

  useEffect(() => {
    loadCaptcha();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    if (!captchaId || captchaValue === "") {
      setToast("Please enter captcha");
      setTimeout(() => setToast(""), 4000);
      setIsSubmitting(false);
      return;
    }

    if (captchaValue !== captchaText) {
      setToast("Captcha must match exactly, including uppercase and lowercase letters");
      loadCaptcha();
      setTimeout(() => setToast(""), 5000);
      setIsSubmitting(false);
      return;
    }

    const res = await dispatch(
      loginUser({
        username: formData.username,
        password: formData.password,
        captchaId,
        captchaValue,
      })
    );

    if (res.meta.requestStatus === "fulfilled") {
      sessionStorage.setItem("adminLogin", "true");
      await dispatch(fetchUserDetails());
      navigate("/", { replace: true });
    } else {
      setToast(res.payload || "Check your credentials and try again");
      loadCaptcha();
      setTimeout(() => setToast(""), 5000);
    }

    setIsSubmitting(false);
  };

  useEffect(() => {
    if (user?.user?.customerId && location.pathname !== "/") {
      navigate("/", { replace: true });
    }
  }, [user?.user?.customerId, navigate, location.pathname]);

  return (
    <div className="px-xl-5 px-4 auth-body">
      <form onSubmit={handleSubmit}>
        <ul className="row g-3 list-unstyled li_animate">

          <h3 className="mb-4 text-center display-9 fw-bold text-primary">
            Admin / Manager Login
          </h3>

          <li className="col-12">
            <label className="form-label">Username</label>
            <input
              type="text"
              name="username"
              className="form-control form-control-lg"
              value={formData.username}
              onChange={handleChange}
              required
            />
          </li>

          <li className="col-12">
            <label className="form-label">Password</label>
            <div className="input-group input-group-lg">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                className="form-control"
                value={formData.password}
                onChange={handleChange}
                required
              />
              <button
                type="button"
                className="btn btn-light border"
                onClick={() => setShowPassword((p) => !p)}
              >
                <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`} />
              </button>
            </div>
          </li>

          {/* CAPTCHA – SAME AS HRMS */}
          <li className="col-12">
            <label className="form-label">Captcha</label>
            <div className="input-group input-group-lg">
              <span
                className="input-group-text fw-bold bg-light"
                style={{
                  letterSpacing: "6px",
                  fontFamily: "monospace",
                  userSelect: "none",
                }}
              >
                {captchaText}
              </span>

              <button
                type="button"
                className="btn"
                onClick={loadCaptcha}
                title="Refresh Captcha"
              >
                ↻
              </button>

              <input
                type="text"
                className="form-control text-center"
                placeholder="Enter captcha"
                value={captchaValue}
                onChange={(e) => setCaptchaValue(e.target.value)}
                autoComplete="off"
                required
              />
            </div>
          </li>

          <li className="col-12 my-4">
            <button
              type="submit"
              className="btn btn-lg w-100 btn-primary"
              disabled={loading || isSubmitting}
            >
              {loading ? "Signing in..." : "SIGN IN"}
            </button>

            {toast && (
              <div className="alert alert-danger mt-3 text-center">
                {toast}
              </div>
            )}

            <Link
              to="/hrms/login"
              onClick={() => {
                sessionStorage.removeItem("adminLogin");
                sessionStorage.removeItem("hrmsLogin");
              }}
              className="d-block mt-2 text-decoration-underline text-primary"
            >
              Login to HRMS
            </Link>
          </li>

          {error && (
            <li className="col-12">
              <div className="alert alert-danger text-center">
                Check your credentials and try again
              </div>
            </li>
          )}

        </ul>
      </form>
    </div>
  );
};

export default Signin;
